import React, { useEffect, useState, useRef } from "react";
import { Plus, Pencil, Trash2, X, Save, Upload, Loader2, User, GripVertical } from "lucide-react";
import { adminApi, mediaUrl } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { useToast } from "../../hooks/use-toast";

const emptyStaff = () => ({ name: "", role: "", bio: "", photo1: "", photo2: "", order: 99 });

function PhotoUpload({ label, value, onChange, hint }) {
  const { toast } = useToast();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await adminApi.uploadImage(fd);
      onChange(res.url);
      toast({ title: "Image uploaded" });
    } catch (err) {
      toast({ title: "Upload failed", description: err?.response?.data?.detail || "Try a smaller image", variant: "destructive" });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      <Label className="text-navy font-semibold">{label}</Label>
      {hint && <p className="text-xs text-slate-400 mt-0.5">{hint}</p>}
      <div className="mt-2 flex items-center gap-4">
        <div className="w-24 h-28 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
          {value ? <img loading="lazy" decoding="async" src={mediaUrl(value)} alt="" className="w-full h-full object-cover" /> : <User className="text-slate-300" size={28} />}
        </div>
        <div className="flex flex-col gap-2">
          <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
          <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading} className="rounded-full">
            {uploading ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> Uploading...</> : <><Upload size={15} className="mr-1.5" /> {value ? "Replace" : "Upload"}</>}
          </Button>
          {value && <button type="button" onClick={() => onChange("")} className="text-xs text-red-500 hover:underline text-left">Remove</button>}
        </div>
      </div>
    </div>
  );
}

export default function StaffManager() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyStaff());
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const load = () => { setLoading(true); adminApi.staff.list().then(setItems).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);

  const handleDrop = async (dropIndex) => {
    setOverIndex(null);
    if (dragIndex === null || dragIndex === dropIndex) { setDragIndex(null); return; }
    const reordered = [...items];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    const withOrder = reordered.map((it, i) => ({ ...it, order: i + 1 }));
    setItems(withOrder);
    setDragIndex(null);
    try {
      await adminApi.staffReorder(withOrder.map((i) => i.id));
      toast({ title: "Order updated" });
    } catch {
      toast({ title: "Couldn't save new order", variant: "destructive" });
      load();
    }
  };

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));
  const openCreate = () => { setEditing(null); setForm(emptyStaff()); setOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ ...emptyStaff(), ...item }); setOpen(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, order: Number(form.order || 99) };
      if (editing) await adminApi.staff.update(editing.id, payload);
      else await adminApi.staff.create(payload);
      toast({ title: `Staff ${editing ? "updated" : "added"}` });
      setOpen(false); load();
    } catch (e) {
      toast({ title: "Error saving", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await adminApi.staff.remove(deleteId); toast({ title: "Staff deleted" }); load(); }
    catch { toast({ title: "Error deleting", variant: "destructive" }); }
    finally { setDeleteId(null); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-extrabold text-2xl text-navy">Team / Staff</h1>
          <p className="text-sm text-slate-500">{items.length} members • upload 2 photos each to enable the hover swap</p>
        </div>
        <Button onClick={openCreate} className="bg-orange hover:bg-orange-dark text-white rounded-full"><Plus size={16} className="mr-1.5" /> Add Staff</Button>
      </div>

      {loading ? <p className="text-slate-400">Loading...</p> : (
        <>
        <p className="text-xs text-slate-400 mb-3 flex items-center gap-1.5"><GripVertical size={13} /> Drag the handle to reorder how the team appears on the website.</p>
        <div className="space-y-3">
          {items.map((m, index) => (
            <div
              key={m.id}
              onDragOver={(e) => { e.preventDefault(); setOverIndex(index); }}
              onDrop={() => handleDrop(index)}
              onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
              className={`bg-white rounded-xl border p-4 flex items-center gap-3 transition-all ${overIndex === index && dragIndex !== null ? "border-orange ring-2 ring-orange/30" : "border-slate-100"} ${dragIndex === index ? "opacity-50" : "hover:shadow-sm"}`}
            >
              <button
                draggable
                onDragStart={() => setDragIndex(index)}
                className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-navy shrink-0 touch-none"
                title="Drag to reorder"
              >
                <GripVertical size={18} />
              </button>
              <div className="flex -space-x-3 shrink-0">
                <div className="w-12 h-14 rounded-lg bg-slate-100 border-2 border-white overflow-hidden flex items-center justify-center">
                  {m.photo1 ? <img loading="lazy" decoding="async" src={mediaUrl(m.photo1)} alt="" className="w-full h-full object-cover" /> : <User size={16} className="text-slate-300" />}
                </div>
                <div className="w-12 h-14 rounded-lg bg-slate-100 border-2 border-white overflow-hidden flex items-center justify-center">
                  {m.photo2 ? <img loading="lazy" decoding="async" src={mediaUrl(m.photo2)} alt="" className="w-full h-full object-cover" /> : <User size={16} className="text-slate-300" />}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-navy truncate">{m.name}</p>
                <p className="text-sm text-slate-400 truncate">{m.role}</p>
              </div>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 shrink-0">#{m.order}</span>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => openEdit(m)} className="w-9 h-9 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-navy transition-colors"><Pencil size={15} /></button>
                <button onClick={() => setDeleteId(m.id)} className="w-9 h-9 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center text-red-500 transition-colors"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Staff Member" : "Add Staff Member"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-navy font-semibold">Name</Label><Input value={form.name} onChange={(e) => setField("name", e.target.value)} className="mt-2" placeholder="Paul" /></div>
              <div><Label className="text-navy font-semibold">Role</Label><Input value={form.role} onChange={(e) => setField("role", e.target.value)} className="mt-2" placeholder="Founder" /></div>
            </div>
            <div><Label className="text-navy font-semibold">Sort order</Label><Input type="number" value={form.order} onChange={(e) => setField("order", e.target.value)} className="mt-2" /></div>
            <div>
              <Label className="text-navy font-semibold">Bio</Label>
              <Textarea rows={5} value={form.bio} onChange={(e) => setField("bio", e.target.value)} className="mt-2" placeholder="A short bio. Leave a blank line to start a new paragraph." />
              <p className="text-xs text-slate-400 mt-1">Tip: press Enter twice to separate paragraphs.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-6 pt-2 border-t border-slate-100">
              <PhotoUpload label="Photo 1 (default)" hint="Shown normally" value={form.photo1} onChange={(v) => setField("photo1", v)} />
              <PhotoUpload label="Photo 2 (on hover)" hint="Swaps in when hovered" value={form.photo2} onChange={(v) => setField("photo2", v)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full"><X size={15} className="mr-1" /> Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-orange hover:bg-orange-dark text-white rounded-full"><Save size={15} className="mr-1" /> {saving ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this staff member?</AlertDialogTitle><AlertDialogDescription>This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 rounded-full">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
